import app from 'flarum/forum/app';
import Component from 'flarum/common/Component';
import icon from 'flarum/common/helpers/icon';

const normalize = (name) => String(name || '').trim().replace(/^#+/, '').toLocaleLowerCase();
const TOPIC_SHORTCUT_EVENT = 'space-pocket-posts:open-topics';

export default class TopicComposer extends Component {
  oninit(vnode) {
    super.oninit(vnode);
    this.open = false;
    this.loading = false;
    this.query = '';
    this.results = [];
    this.searchTimer = null;
    this.requestId = 0;
  }

  oncreate(vnode) {
    super.oncreate(vnode);
    this.loadTopics();
    this.onDocumentClick = (event) => {
      if (this.open && this.element && !this.element.contains(event.target)) {
        this.open = false;
        m.redraw();
      }
    };
    this.onTopicShortcut = () => this.openPanel();
    document.addEventListener('click', this.onDocumentClick);
    document.addEventListener(TOPIC_SHORTCUT_EVENT, this.onTopicShortcut);
  }

  onremove(vnode) {
    if (this.searchTimer) window.clearTimeout(this.searchTimer);
    document.removeEventListener('click', this.onDocumentClick);
    document.removeEventListener(TOPIC_SHORTCUT_EVENT, this.onTopicShortcut);

    const composer = this.attrs.composer;

    if (composer.spacePocketTopicsDiscussion && !composer.spacePocketTopicsSubmitted) {
      composer.spacePocketTopicsDiscussion.pushAttributes({
        spacePocketTopics: JSON.parse(composer.spacePocketOriginalTopics || '[]'),
      });
    }

    super.onremove(vnode);
  }

  selected() {
    const composer = this.attrs.composer;

    if (!Array.isArray(composer.spacePocketTopics)) composer.spacePocketTopics = [];

    return composer.spacePocketTopics;
  }

  syncDiscussion() {
    const composer = this.attrs.composer;
    const discussion = composer.spacePocketTopicsDiscussion;

    if (discussion && typeof discussion.pushAttributes === 'function') {
      discussion.pushAttributes({ spacePocketTopics: [...this.selected()] });
    }
  }

  async loadTopics(query = '') {
    const requestId = ++this.requestId;
    this.loading = true;

    try {
      const suffix = query ? `?filter[q]=${encodeURIComponent(query)}` : '';
      const response = await app.request({
        method: 'GET',
        url: app.forum.attribute('apiUrl') + '/space-pocket/topics' + suffix,
      });

      if (requestId !== this.requestId) return;

      this.results = (Array.isArray(response.data) ? response.data : []).map((resource) => ({
        id: String(resource.id),
        name: resource.attributes.name,
        useCount: resource.attributes.useCount || 0,
        featured: Boolean(resource.attributes.featured),
      }));
    } catch (error) {
      if (requestId === this.requestId) this.results = [];
    } finally {
      if (requestId === this.requestId) {
        this.loading = false;
        m.redraw();
      }
    }
  }

  search(event) {
    this.query = event.currentTarget.value;

    if (this.searchTimer) window.clearTimeout(this.searchTimer);
    this.searchTimer = window.setTimeout(() => this.loadTopics(this.query.trim()), 220);
  }

  focusSearch() {
    window.requestAnimationFrame(() => {
      const input = this.element && this.element.querySelector('.SpacePocketPosts-topicSearch > input');

      if (input) input.focus();
    });
  }

  openPanel() {
    this.open = true;

    if (!this.results.length && !this.loading) this.loadTopics();

    m.redraw();
    this.focusSearch();
  }

  togglePanel(event) {
    event.preventDefault();
    event.stopPropagation();

    if (this.open) {
      this.open = false;
      return;
    }

    this.openPanel();
  }

  handleSearchKeydown(event) {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.open = false;
      m.redraw();
      return;
    }

    if (event.key !== 'Enter') return;

    event.preventDefault();
    event.stopPropagation();

    const query = this.query.trim().replace(/^#+/, '');
    const exact = this.results.find((topic) => normalize(topic.name) === normalize(query));

    if (exact) {
      this.toggleTopic(exact.name);
      this.query = '';
      this.loadTopics();
    } else if (this.canCreate()) {
      this.createTopic();
    } else if (this.results.length) {
      this.toggleTopic(this.results[0].name);
    }
  }

  toggleTopic(name, event) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    const topics = this.selected();
    const key = normalize(name);
    const index = topics.findIndex((topic) => normalize(topic) === key);

    if (index !== -1) {
      topics.splice(index, 1);
      this.syncDiscussion();
      m.redraw();
      return;
    }

    const maxTopics = Number(app.forum.attribute('spacePocketMaxTopics')) || 5;

    if (topics.length >= maxTopics) {
      app.alerts.show(
        { type: 'error' },
        app.translator.trans('space-pocket-posts.forum.topics.max_topics', { count: maxTopics })
      );
      return;
    }

    topics.push(String(name).trim().replace(/^#+/, ''));
    this.syncDiscussion();
    m.redraw();
  }

  canCreate() {
    const name = this.query.trim().replace(/^#+/, '');

    return (
      Boolean(app.forum.attribute('spacePocketAllowCustomTopics')) &&
      name.length > 0 &&
      name.length <= 30 &&
      !/[#@"\u0000-\u001f\u007f]/.test(name) &&
      !this.results.some((topic) => normalize(topic.name) === normalize(name))
    );
  }

  createTopic(event) {
    const name = this.query.trim().replace(/^#+/, '');

    if (!this.canCreate()) return;

    this.toggleTopic(name, event);
    this.query = '';
    this.loadTopics();
  }

  topicButton(topic, quick = false) {
    const selected = this.selected().some((name) => normalize(name) === normalize(topic.name));

    return (
      <button
        type="button"
        className={'SpacePocketPosts-topicChip ' + (selected ? 'is-selected ' : '') + (quick ? 'is-quick' : '')}
        aria-pressed={selected ? 'true' : 'false'}
        onclick={(event) => this.toggleTopic(topic.name, event)}
      >
        <span aria-hidden="true">#</span>{topic.name}
        {topic.featured && !quick && <span className="SpacePocketPosts-featuredIcon">{icon('fas fa-thumbtack')}</span>}
      </button>
    );
  }

  view() {
    const selected = this.selected();
    const createName = this.query.trim().replace(/^#+/, '');

    return (
      <div className={'SpacePocketPosts-topicComposer ' + (this.open ? 'is-open' : '')}>
        <button
          type="button"
          className="Button Button--icon SpacePocketPosts-topicTrigger"
          aria-label={app.translator.trans('space-pocket-posts.forum.topics.open')}
          aria-expanded={this.open ? 'true' : 'false'}
          onclick={(event) => this.togglePanel(event)}
        >
          <span className="SpacePocketPosts-hashIcon" aria-hidden="true">#</span>
          <span>{app.translator.trans('space-pocket-posts.forum.topics.label')}</span>
          {selected.length > 0 && <span className="SpacePocketPosts-topicCount">{selected.length}</span>}
        </button>

        {!this.open && this.results.length > 0 && (
          <div className="SpacePocketPosts-topicQuickList">
            {this.results.map((topic) => this.topicButton(topic, true))}
          </div>
        )}

        {this.open && (
          <div className="SpacePocketPosts-topicPanel" onclick={(event) => event.stopPropagation()}>
            <div className="SpacePocketPosts-topicPanelHeader">
              <strong>{app.translator.trans('space-pocket-posts.forum.topics.title')}</strong>
              <button
                type="button"
                className="Button Button--icon"
                aria-label={app.translator.trans('space-pocket-posts.forum.topics.close')}
                onclick={(event) => this.togglePanel(event)}
              >
                {icon('fas fa-times')}
              </button>
            </div>

            {selected.length > 0 && (
              <div className="SpacePocketPosts-selectedTopics">
                {selected.map((name) => this.topicButton({ name }))}
              </div>
            )}

            <label className="SpacePocketPosts-topicSearch">
              {icon('fas fa-search')}
              <input
                type="search"
                value={this.query}
                maxlength="30"
                placeholder={app.translator.trans('space-pocket-posts.forum.topics.search')}
                oninput={(event) => this.search(event)}
                onkeydown={(event) => this.handleSearchKeydown(event)}
              />
            </label>

            <div className="SpacePocketPosts-topicResults">
              {this.loading && <span className="SpacePocketPosts-topicStatus">{icon('fas fa-spinner', { className: 'fa-spin' })}</span>}
              {!this.loading && this.results.map((topic) => this.topicButton(topic))}
              {!this.loading && this.canCreate() && (
                <button type="button" className="SpacePocketPosts-createTopic" onclick={(event) => this.createTopic(event)}>
                  {icon('fas fa-plus')}
                  <span>#{createName}</span>
                </button>
              )}
              {!this.loading && !this.results.length && !this.canCreate() && (
                <span className="SpacePocketPosts-topicStatus">
                  {app.translator.trans('space-pocket-posts.forum.topics.empty')}
                </span>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }
}
